import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../store.jsx";
import { Icon, StatCard } from "../../components/Common.jsx";
import { getHolidays, getMyLeaves, cancelMyLeave } from "../../lib/api";

const fmtShort = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const fmtFull = (d) =>
  new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const fmtDay = (d) =>
  new Date(d).toLocaleDateString("en-IN", { weekday: "long" });

const daysUntil = (d) => {
  const now = new Date(); now.setUTCHours(0, 0, 0, 0);
  const diff = Math.ceil((new Date(d) - now) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return `In ${diff} days`;
};

export default function MyCalendar() {
  const { toast, openModal, closeModal } = useApp();
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState("holidays");

  const refresh = async () => {
    try {
      const [h, l] = await Promise.all([getHolidays(year), getMyLeaves()]);
      setHolidays(Array.isArray(h) ? h : []);
      setLeaves(Array.isArray(l) ? l : []);
    } catch (e) {
      toast(e.message || "Failed to load", "error");
    }
  };

  useEffect(() => { refresh(); }, [year]); // eslint-disable-line

  const today = useMemo(() => { const d = new Date(); d.setUTCHours(0, 0, 0, 0); return d; }, []);

  const upcomingHolidays = useMemo(() =>
    holidays.filter((h) => new Date(h.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date)),
    [holidays, today]);
  const pastHolidays = useMemo(() =>
    holidays.filter((h) => new Date(h.date) < today).sort((a, b) => new Date(b.date) - new Date(a.date)),
    [holidays, today]);

  const futureLeaves = useMemo(() =>
    leaves.filter((l) => new Date(l.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date)),
    [leaves, today]);
  const pastLeaves = useMemo(() =>
    leaves.filter((l) => new Date(l.date) < today).sort((a, b) => new Date(b.date) - new Date(a.date)),
    [leaves, today]);

  const nextHoliday = upcomingHolidays[0] || null;

  const onCancelLeave = (lv) => {
    const dateLabel = fmtFull(lv.date);
    const doCancel = async () => {
      try {
        await cancelMyLeave(lv.id);
        toast("Leave cancelled", "success");
        closeModal();
        refresh();
      } catch (e) {
        toast(e.message || "Cancel failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Cancel Leave</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p>Cancel your leave for <strong>{dateLabel}</strong>?</p>
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            You will be expected to submit your daily report on this day.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Keep Leave</button>
          <button className="btn btn-danger" onClick={doCancel}>Cancel Leave</button>
        </div>
      </>,
      "md",
    );
  };

  const currentYear = new Date().getFullYear();

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">My Calendar</h1>
          <p className="page-sub">Company holidays and your leave history</p>
        </div>
        <select
          className="input"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: 100, fontSize: 13, padding: "6px 10px" }}
        >
          {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {nextHoliday && (
        <div className="card" style={{
          background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(124,58,237,0.06))",
          borderColor: "rgba(139,92,246,0.4)",
          marginBottom: 18,
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: "rgba(139,92,246,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Icon name="gift" size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>Next Holiday</div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{nextHoliday.name}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
              {fmtFull(nextHoliday.date)}
            </div>
          </div>
          <div style={{
            background: "rgba(139,92,246,0.2)", borderRadius: 10,
            padding: "8px 16px", textAlign: "center",
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#a78bfa" }}>{fmtShort(nextHoliday.date).split(" ")[0]}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>{fmtShort(nextHoliday.date).split(" ")[1]}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#a78bfa", marginTop: 2 }}>{daysUntil(nextHoliday.date)}</div>
          </div>
        </div>
      )}

      <div className="stats-row">
        <StatCard label="Total Holidays" value={holidays.length} icon="calendar" color="primary" />
        <StatCard label="Upcoming" value={upcomingHolidays.length} icon="calendar-check" color="green" />
        <StatCard label="Leaves Taken" value={leaves.length} icon="calendar-x" color="amber" />
        <StatCard label="Upcoming Leaves" value={futureLeaves.length} icon="clock" color="red" />
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {[
          ["holidays", `Holidays (${holidays.length})`, "calendar"],
          ["leaves", `My Leaves (${leaves.length})`, "calendar-x"],
        ].map(([k, label, icon]) => (
          <button
            key={k}
            className={`filter-chip ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)}
          >
            <Icon name={icon} size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "holidays" && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Company Holidays — {year}</div>
              <div className="card-subtitle">{upcomingHolidays.length} upcoming, {pastHolidays.length} past</div>
            </div>
          </div>

          {!holidays.length ? (
            <div className="empty-state">
              <div className="ico"><Icon name="calendar" size={28} /></div>
              <h3>No holidays</h3>
              <p>No holidays have been added for {year} yet.</p>
            </div>
          ) : (
            <>
              {upcomingHolidays.length > 0 && (
                <div style={{ padding: "0 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    Upcoming
                  </div>
                  {upcomingHolidays.map((h) => (
                    <div key={h.id} className="activity-row">
                      <div style={{
                        width: 44, minHeight: 44, borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(124,58,237,0.1))",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, padding: "4px 0",
                      }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{new Date(h.date).getUTCDate()}</div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>
                          {new Date(h.date).toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                      </div>
                      <div className="activity-text" style={{ flex: 1 }}>
                        <div className="activity-title" style={{ fontWeight: 600 }}>{h.name}</div>
                        <div className="activity-meta">{fmtDay(h.date)} &middot; {daysUntil(h.date)}</div>
                      </div>
                      <span className="badge" style={{ fontSize: 10 }}>{h.type}</span>
                    </div>
                  ))}
                </div>
              )}

              {pastHolidays.length > 0 && (
                <div style={{ padding: "12px 16px 8px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    Past
                  </div>
                  {pastHolidays.map((h) => (
                    <div key={h.id} className="activity-row" style={{ opacity: 0.5 }}>
                      <div style={{
                        width: 44, minHeight: 44, borderRadius: 10,
                        background: "rgba(148,163,184,0.1)",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, padding: "4px 0",
                      }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{new Date(h.date).getUTCDate()}</div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>
                          {new Date(h.date).toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                      </div>
                      <div className="activity-text" style={{ flex: 1 }}>
                        <div className="activity-title" style={{ fontWeight: 600 }}>{h.name}</div>
                        <div className="activity-meta">{fmtDay(h.date)}</div>
                      </div>
                      <span className="badge" style={{ fontSize: 10 }}>{h.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "leaves" && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">My Leaves</div>
              <div className="card-subtitle">{futureLeaves.length} upcoming, {pastLeaves.length} past</div>
            </div>
          </div>

          {!leaves.length ? (
            <div className="empty-state">
              <div className="ico"><Icon name="calendar-x" size={28} /></div>
              <h3>No leaves</h3>
              <p>You haven't taken any leaves yet.</p>
            </div>
          ) : (
            <>
              {futureLeaves.length > 0 && (
                <div style={{ padding: "0 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    Upcoming / Today
                  </div>
                  {futureLeaves.map((lv) => (
                    <div key={lv.id} className="activity-row">
                      <div style={{
                        width: 44, minHeight: 44, borderRadius: 10,
                        background: "rgba(239,68,68,0.12)",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, padding: "4px 0",
                      }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{new Date(lv.date).getUTCDate()}</div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>
                          {new Date(lv.date).toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                      </div>
                      <div className="activity-text" style={{ flex: 1 }}>
                        <div className="activity-title" style={{ fontWeight: 600 }}>{fmtDay(lv.date)}, {fmtShort(lv.date)}</div>
                        <div className="activity-meta">
                          {lv.reason || `via ${lv.source || "web"}`} &middot; {daysUntil(lv.date)}
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 12, color: "var(--error)", borderColor: "rgba(239,68,68,0.3)" }}
                        onClick={() => onCancelLeave(lv)}
                      >
                        <Icon name="x" size={13} /> Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {pastLeaves.length > 0 && (
                <div style={{ padding: "12px 16px 8px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                    Past
                  </div>
                  {pastLeaves.map((lv) => (
                    <div key={lv.id} className="activity-row" style={{ opacity: 0.5 }}>
                      <div style={{
                        width: 44, minHeight: 44, borderRadius: 10,
                        background: "rgba(148,163,184,0.1)",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, padding: "4px 0",
                      }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>{new Date(lv.date).getUTCDate()}</div>
                        <div style={{ fontSize: 9, textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 600 }}>
                          {new Date(lv.date).toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                      </div>
                      <div className="activity-text" style={{ flex: 1 }}>
                        <div className="activity-title" style={{ fontWeight: 600 }}>{fmtDay(lv.date)}, {fmtShort(lv.date)}</div>
                        <div className="activity-meta">{lv.reason || `via ${lv.source || "web"}`}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
